import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { CloudUpload } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RESIDENT_DOCUMENT_FILE_ACCEPT } from '../../constants/residentConstants'

/**
 * Matches Angular `resident-edit-new` Liberty Internal uploads (`custom_file_upload_wrap` /
 * `file_upload_inner` in `form-elements.scss`): filename strip + full-opacity overlay
 * `input[type=file]` + trailing “Upload” / “Uploading …”.
 */
export function LibertyInternalFileUploadRow({
  multiple = false,
  disabled = false,
  uploading = false,
  onFilesSelected,
  onOversizedFile,
  className,
  variant = 'bar',
  buttonLabel = 'Add Documents',
  accept = RESIDENT_DOCUMENT_FILE_ACCEPT,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: {
  multiple?: boolean
  disabled?: boolean
  uploading?: boolean
  onFilesSelected: (files: File[]) => void
  /** Angular notifies when any file exceeds 10 MB. */
  onOversizedFile?: () => void
  className?: string
  /** `bar` = classic choose-file strip; `addButton` = outlined header action. */
  variant?: 'bar' | 'addButton'
  buttonLabel?: string
  accept?: string
  'aria-label'?: string
  'data-testid'?: string
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [fileNames, setFileNames] = useState('')
  const [pendingLargeFile, setPendingLargeFile] = useState<{
    list: File[]
    message: string
  } | null>(null)
  const prevUploading = useRef(uploading)

  useEffect(() => {
    if (prevUploading.current && !uploading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear the staged file names after an upload completes
      setFileNames('')
    }
    prevUploading.current = uploading
  }, [uploading])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) {
      e.target.value = ''
      return
    }
    const list = Array.from(files)
    const tenMb = 10 * 1024 * 1024
    const oneMb = 1 * 1024 * 1024
    const oversized = list.find((f) => f.size > tenMb)
    if (oversized) {
      onOversizedFile?.()
      e.target.value = ''
      return
    }
    // Angular selectFileDocuments (resident-edit-new.component.ts:2409-2438): any file >1MB
    // (and <=10MB) requires an explicit "large file" confirm before upload; on decline reset.
    const largeFileText = list
      .filter((f) => f.size > oneMb)
      .map((f) => `\n${f.name} - ${(f.size / 1024 / 1024).toFixed(2)} MB`)
      .join('')
    if (largeFileText) {
      setPendingLargeFile({
        list,
        message: `Please confirm, that you really want to continue to upload a large file${largeFileText}`,
      })
      e.target.value = ''
      return
    }
    setFileNames(list.map((f) => f.name).join(', '))
    onFilesSelected(list)
    e.target.value = ''
  }

  const handleConfirmLargeFile = () => {
    if (!pendingLargeFile) return
    const list = pendingLargeFile.list
    setPendingLargeFile(null)
    setFileNames(list.map((f) => f.name).join(', '))
    onFilesSelected(list)
  }

  const handleCancelLargeFile = () => {
    if (inputRef.current) inputRef.current.value = ''
    setFileNames('')
    setPendingLargeFile(null)
  }

  const busy = disabled || uploading

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      multiple={multiple}
      accept={accept}
      disabled={busy}
      aria-label={ariaLabel}
      className="sr-only"
      onChange={handleChange}
    />
  )

  if (variant === 'addButton') {
    return (
      <div data-testid={dataTestId} className={cn('inline-flex', className)}>
        {fileInput}
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#1597ff] bg-white px-3 text-sm font-semibold text-[#1597ff] shadow-sm transition hover:bg-[#eef7ff] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <CloudUpload className="h-4 w-4" aria-hidden />
          {uploading ? 'Uploading…' : buttonLabel}
        </button>
      </div>
    )
  }

  return (
    <>
      <div
        data-testid={dataTestId}
        className={cn(
          'flex min-h-[2.5rem] overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm',
          className,
        )}
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className={`min-w-0 flex-1 truncate px-3 py-2 text-left text-sm font-normal transition ${
            fileNames ? 'text-slate-700' : 'text-slate-400'
          } hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {fileNames || 'Choose file...'}
        </button>
        {fileInput}
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 border-l border-slate-300 bg-[rgb(21_151_255)]/10 px-4 py-2 text-sm font-semibold text-[rgb(21_151_255)] transition hover:bg-[rgb(21_151_255)]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {uploading ? 'Uploading ...' : 'Upload'}
        </button>
      </div>

      <ConfirmDialog
        isOpen={pendingLargeFile != null}
        title="Large File"
        message={pendingLargeFile?.message ?? ''}
        primaryLabel="Yes"
        onPrimary={handleConfirmLargeFile}
        secondaryLabel="No"
        onSecondary={handleCancelLargeFile}
        onDismiss={handleCancelLargeFile}
        messageLayout="largeFile"
        primaryFirst
        actionStyle="application"
        sectionedChrome
      />
    </>
  )
}
